import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { 
  MapPin, Truck, Package, CheckCircle, Clock, AlertCircle, Phone, Building,
  Loader2, Calendar, ArrowRight, User, Shield
} from 'lucide-react';
import api from '../services/api';

const ShipmentTrackingPage = () => {
  const { trackingToken } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTracking();
  }, [trackingToken]);

  const fetchTracking = async () => {
    try {
      const response = await api.get(`/track/${trackingToken}`);
      setTracking(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Tracking not found' : 'Failed to load tracking information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'in_transit':
      case 'picked_up':
        return <Truck className="w-6 h-6 text-blue-400" />;
      case 'assigned':
        return <User className="w-6 h-6 text-purple-400" />;
      default:
        return <Package className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-500';
      case 'in_transit':
      case 'picked_up':
        return 'bg-blue-500';
      case 'assigned':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'auto_transport':
        return <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">🚗 Auto Transport</span>;
      case 'hotshot':
        return <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">⚡ Hotshot</span>;
      case 'freight':
        return <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">📦 Freight</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#4ADE80] mx-auto mb-4" />
          <p className="text-gray-400">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-red-500/20 bg-[#0D1B2A]">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Tracking Not Found</h2>
            <p className="text-gray-400 mb-6">
              {error}. Please check your tracking link and try again.
            </p>
            <Link 
              to="/" 
              className="text-[#4ADE80] hover:underline"
            >
              Return to HaulLogix
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423]">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <Shield className="w-10 h-10 text-[#4ADE80]" />
                <Truck className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-white">HAUL</span>
                <span className="text-[#4ADE80]">LOGIX</span>
              </span>
            </Link>
            <span className="text-sm text-gray-400">Shipment Tracking</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Banner */}
        <Card className={`border-0 mb-6 ${
          tracking.current_status === 'delivered' ? 'bg-gradient-to-r from-green-500/20 to-green-600/10' :
          tracking.current_status === 'in_transit' ? 'bg-gradient-to-r from-blue-500/20 to-blue-600/10' :
          'bg-gradient-to-r from-purple-500/20 to-purple-600/10'
        }`}>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${
                  tracking.current_status === 'delivered' ? 'bg-green-500/20' :
                  tracking.current_status === 'in_transit' ? 'bg-blue-500/20' :
                  'bg-purple-500/20'
                }`}>
                  {getStatusIcon(tracking.current_status)}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">{tracking.status_message}</h1>
                  <p className="text-gray-400 mt-1">
                    {tracking.current_status === 'delivered' ? 'Your shipment has arrived!' : 
                     tracking.current_status === 'in_transit' ? 'Your shipment is on the way' :
                     'We\'re preparing your shipment'}
                  </p>
                </div>
              </div>
              {getCategoryBadge(tracking.load_category)}
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Route Card */}
          <div className="md:col-span-2">
            <Card className="border-[#3B82F6]/20 bg-[#0D1B2A] h-full">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#4ADE80]" />
                  Route Information
                </h2>
                
                <div className="flex items-center gap-4 mb-8">
                  {/* Origin */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full bg-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-1">Pickup</p>
                        <p className="text-lg font-semibold text-white">{tracking.pickup_location}</p>
                        <p className="text-sm text-gray-400 mt-1">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {formatDate(tracking.pickup_date)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex-shrink-0">
                    <ArrowRight className="w-8 h-8 text-gray-600" />
                  </div>
                  
                  {/* Destination */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full bg-red-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-1">Delivery</p>
                        <p className="text-lg font-semibold text-white">{tracking.delivery_location}</p>
                        {tracking.estimated_delivery && (
                          <p className="text-sm text-gray-400 mt-1">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Est. {formatDate(tracking.estimated_delivery)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="border-t border-[#3B82F6]/10 pt-6">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4">Tracking History</h3>
                  <div className="space-y-4">
                    {tracking.timeline.slice().reverse().map((event, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(event.status)}`} />
                          {index < tracking.timeline.length - 1 && (
                            <div className="w-0.5 h-full bg-[#3B82F6]/20 mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="text-white font-medium">{event.message}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                            <span>{formatDate(event.timestamp)}</span>
                            <span>{formatTime(event.timestamp)}</span>
                            {event.location && (
                              <>
                                <span>•</span>
                                <span>{event.location}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            {/* Driver Info */}
            {tracking.driver_first_name && (
              <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4">Your Driver</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-[#3B82F6]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{tracking.driver_first_name}</p>
                      <p className="text-xs text-gray-400">Assigned Driver</p>
                    </div>
                  </div>
                  {tracking.driver_phone && (
                    <a 
                      href={`tel:${tracking.driver_phone}`}
                      className="flex items-center gap-2 text-[#4ADE80] hover:text-[#22C55E] transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{tracking.driver_phone}</span>
                    </a>
                  )}
                  {!tracking.driver_phone && tracking.current_status === 'assigned' && (
                    <p className="text-xs text-gray-500">
                      Driver contact will be available after pickup
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Company Info */}
            {tracking.company_name && (
              <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4">Carrier</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Building className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{tracking.company_name}</p>
                      <p className="text-xs text-gray-400">Transport Company</p>
                    </div>
                  </div>
                  {tracking.company_phone && (
                    <a 
                      href={`tel:${tracking.company_phone}`}
                      className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{tracking.company_phone}</span>
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Need Help */}
            <Card className="border-[#3B82F6]/10 bg-[#112240]/50">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-white mb-2">Need Help?</h3>
                <p className="text-sm text-gray-400 mb-3">
                  If you have questions about your shipment, contact the carrier using the information above.
                </p>
                <Link 
                  to="/contact" 
                  className="text-sm text-[#60A5FA] hover:underline"
                >
                  Contact HaulLogix Support
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 py-6 border-t border-[#3B82F6]/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Powered by <Link to="/" className="text-[#4ADE80] hover:underline">HaulLogix</Link> - 
            The trusted platform for auto transport, hotshot, and freight
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShipmentTrackingPage;
